import {
	FormControl,
	FormErrorMessage,
	Flex,
	Switch,
	FormLabel,
	forwardRef,
} from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import Select from "../inputs/Select";
import { useEffect, useImperativeHandle } from "react";
import _ from "lodash";
import {
	type TicketLabel,
	TicketPriority,
	TicketScope,
} from "../../types/ticket.types";

type CreateLabelForm = {
	defaultValue: TicketLabel | null;
};

export type CreateLabelFormHandler = {
	values: () => TicketLabel;
	isValid: () => boolean;
	isDirty: () => boolean;
	reset: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreateLabelForm = forwardRef(({ defaultValue }, ref) => {
	const methods = useForm();
	useEffect(() => {
		if (defaultValue) {
			methods.reset({
				label: defaultValue.label,
				priority: defaultValue.priority,
				scope: defaultValue.scope,
				proof: defaultValue.proof,
				published: defaultValue.published,
			});
		} else {
			methods.reset({
				label: "",
				priority: TicketPriority.LOW,
				scope: TicketScope.ALL,
				proof: false,
				published: false,
			});
		}
	}, [methods, defaultValue]);
	useImperativeHandle(ref, () => ({
		values: () => methods.getValues(),
		isValid: () => methods.trigger(),
		reset: () =>
			methods.reset({
				label: "",
				priority: TicketPriority.LOW,
				scope: TicketScope.ALL,
				proof: false,
				published: false,
			}),
		isDirty: () => {
			return !_.isEqual(methods.getValues(), {
				label: "",
				priority: TicketPriority.LOW,
				scope: TicketScope.ALL,
				proof: false,
				published: false,
			});
		},
	}));
	return (
		<FormProvider {...methods}>
			<form style={{ marginTop: "32px", marginBottom: "32px" }}>
				<Flex direction="column" gap="24px">
					<FormControl isInvalid={!!methods.formState.errors.label}>
						<Input
							id="label"
							defaultValue={defaultValue?.label ?? undefined}
							placeholder="Nom du motif"
							{...methods.register("label", {
								required: "Ce champ est obligatoire",
							})}
						></Input>
						<FormErrorMessage>
							<>{methods.formState.errors?.label?.message}</>
						</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!methods.formState.errors.priority}>
						<Select
							id="scope"
							placeholder="Utilisateurs"
							defaultValue={defaultValue?.scope ?? TicketScope.ALL}
							{...methods.register("scope", {
								required: "Ce champ est obligatoire",
							})}
						>
							<option value="ALL">Tout le monde</option>
							<option value="SUP">Supembouts</option>
							<option value="CLIENTS">Audio</option>
						</Select>
						<FormErrorMessage>
							<>{methods.formState.errors?.priority?.message}</>
						</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!methods.formState.errors.priority}>
						<Select
							id="priority"
							placeholder="Priorité"
							defaultValue={defaultValue?.priority ?? "LOW"}
							{...methods.register("priority", {
								required: "Ce champ est obligatoire",
							})}
						>
							<option value="LOW">Normal</option>
							<option value="MEDIUM">Moyenne</option>
							<option value="HIGH">Haute</option>
						</Select>
						<FormErrorMessage>
							<>{methods.formState.errors?.priority?.message}</>
						</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!methods.formState.errors.proof}>
						<Switch
							id="proof"
							placeholder="Preuve"
							colorScheme="brand"
							size="lg"
							{...methods.register("proof")}
							mr={4}
						></Switch>
						<FormLabel htmlFor="proof" mb="0" display={"inline"}>
							Demander une photo
						</FormLabel>
						<FormErrorMessage>
							<>{methods.formState.errors?.proof?.message}</>
						</FormErrorMessage>
					</FormControl>
					<FormControl isInvalid={!!methods.formState.errors.published}>
						<Switch
							id="published"
							placeholder="Preuve"
							colorScheme="brand"
							size="lg"
							{...methods.register("published")}
							mr={4}
						></Switch>
						<FormLabel htmlFor="published" mb="0" display={"inline"}>
							Publier ce libellé
						</FormLabel>
						<FormErrorMessage>
							<>{methods.formState.errors?.published?.message}</>
						</FormErrorMessage>
					</FormControl>
				</Flex>
			</form>
		</FormProvider>
	);
});

export default CreateLabelForm;
